import React from 'react';
import {
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import WarningIcon from '@material-ui/icons/Warning';
import { Autocomplete } from '@material-ui/lab';
import clsx from 'clsx';
import { Spinner } from '../../components/UI';
import useApprovalWorkflow from './useApprovalWorkflow';
import {
    approvalColumnsFor,
    REQUEST_STATUSES,
    REQUEST_TYPES,
    employeeModeOf,
    barsRejoining,
    lastWorkingDateOf,
    reasonOf,
    riderNameOf,
} from './approval.helper';
import {
    UserTableCell,
    UserTableHeadCell,
} from '../user-management/utility.component.user';
import ApprovalDecisionModal from '../../components/Modals/ApprovalDecisionModal';
import { roleLabel } from '../user-management/user.helper';

const formatDateTime = (value?: string) =>
    value ? new Date(value).toLocaleString('en-IN') : '-';

const ApprovalWorkflowPage: React.FC = () => {
    const {
        styles,
        canDecide,
        requestType,
        status,
        requests,
        counts,
        totalCount,
        pageNo,
        pageSize,
        loading,
        rehireConfirmationRequired,
        decisionRow,
        setDecisionRow,
        handleTypeChange,
        handleStatusChange,
        handleChangePage,
        handleChangeRowsPerPage,
        handleApprove,
        handleReject,
        refetch,
        orgIds,
        storeIds,
        zones,
        storeOptions,
        zoneOptions,
        vendorOrgs,
        setOrgIds,
        setStoreIds,
        setZones,
    } = useApprovalWorkflow();

    const columns = approvalColumnsFor(requestType);

    const renderCell = (row: any, key: string) => {
        switch (key) {
            case 'riderName':
                return riderNameOf(row);
            case 'reason':
                // The consequence rides with the reason: an approver deciding from the list
                // should not have to open the request to learn the rider cannot come back.
                return (
                    <Box>
                        <div>{reasonOf(row)}</div>
                        {barsRejoining(row) && (
                            <div style={{ fontSize: 12, color: '#c62828' }}>
                                Not eligible for rejoining
                            </div>
                        )}
                    </Box>
                );
            case 'lastWorkingDate':
                return lastWorkingDateOf(row);
            case 'employeeMode':
                return employeeModeOf(row);
            case 'createdAt':
                return formatDateTime(row.createdAt);
            case 'lastAction':
                if (!row.decidedAt) return '-';
                // Name first — the role alone does not say who decided it. Older rows predate the
                // name being returned, so they fall back to showing the role on its own.
                //
                // roleLabel because the API sends the enum verbatim (BluedartUserRoleEnum.name()),
                // which rendered raw here as BLUEDART_ZONAL_APPROVER. It is the one screen that
                // shows an approver their own role, so it has to carry the display name too.
                return (
                    <Box>
                        <div>
                            {row.decidedByName || roleLabel(row.decidedByRole)}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {row.decidedByName && row.decidedByRole
                                ? `${roleLabel(row.decidedByRole)} · ${formatDateTime(row.decidedAt)}`
                                : formatDateTime(row.decidedAt)}
                        </div>
                    </Box>
                );
            case 'requestRef':
                return (
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                        <span>{row.requestRef}</span>
                        {/* Still PENDING and looks ordinary, but the last approve attempt failed —
                            without this an approver retries and hits the same error. */}
                        {!!row.lastError && (
                            <Tooltip title={row.lastError}>
                                <WarningIcon
                                    fontSize="small"
                                    style={{ color: '#c62828' }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                );
            case 'actions':
                return (
                    <Button
                        size="small"
                        color="primary"
                        onClick={() => setDecisionRow(row)}
                    >
                        {row.status === 'PENDING' && canDecide
                            ? 'Review'
                            : 'View'}
                    </Button>
                );
            default:
                return row[key] ?? '-';
        }
    };

    return (
        <Paper className={styles.container}>
            <Box className={styles.toolbar}>
                <Box className={styles.typeTabs}>
                    {REQUEST_TYPES.map((type) => (
                        <Button
                            key={type.value}
                            className={clsx(
                                styles.typeTab,
                                requestType === type.value &&
                                    styles.typeTabActive,
                            )}
                            onClick={() => handleTypeChange(type.value)}
                        >
                            {type.label}
                        </Button>
                    ))}
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    {/* Vendor applies to all three types: on an onboarding it is the org that
                        submitted, on a separation the org the rider belongs to. */}
                    <Autocomplete
                        multiple
                        size="small"
                        limitTags={1}
                        options={vendorOrgs}
                        value={orgIds}
                        getOptionLabel={(o: any) =>
                            `${o?.name ?? ''}${
                                o?.vendorCode ? ` (${o.vendorCode})` : ''
                            }`
                        }
                        getOptionSelected={(o: any, v: any) =>
                            o?.orgId === v?.orgId
                        }
                        onChange={(_e: any, v: any) => setOrgIds(v ?? [])}
                        style={{ minWidth: 210 }}
                        renderInput={(params: any) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder={orgIds.length ? '' : 'Vendor'}
                            />
                        )}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        limitTags={1}
                        options={storeOptions}
                        value={storeIds}
                        onChange={(_e: any, v: any) => setStoreIds(v ?? [])}
                        style={{ minWidth: 170 }}
                        renderInput={(params: any) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder={storeIds.length ? '' : 'Store ID'}
                            />
                        )}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        limitTags={1}
                        options={zoneOptions}
                        value={zones}
                        onChange={(_e: any, v: any) => setZones(v ?? [])}
                        style={{ minWidth: 150 }}
                        renderInput={(params: any) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder={zones.length ? '' : 'Zone'}
                            />
                        )}
                    />
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={refetch}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Box className={styles.statusTabs}>
                {REQUEST_STATUSES.map((s) => (
                    <Button
                        key={s.value}
                        size="small"
                        className={clsx(
                            styles.typeTab,
                            status === s.value && styles.typeTabActive,
                        )}
                        onClick={() => handleStatusChange(s.value)}
                    >
                        {s.label}
                        {counts?.[s.value] !== undefined && (
                            <Chip
                                size="small"
                                label={counts[s.value]}
                                style={{ marginLeft: 6 }}
                            />
                        )}
                    </Button>
                ))}
            </Box>
            <Divider />

            <Spinner loading={loading}>
                <>
                    <TableContainer className={styles.tableWrapper}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <UserTableHeadCell
                                            key={column.key}
                                            style={{
                                                width: `${column.width}%`,
                                            }}
                                        >
                                            {column.label}
                                        </UserTableHeadCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {requests.map((row: any) => (
                                    <TableRow
                                        key={row.requestRef}
                                        hover
                                        className={
                                            row.lastError
                                                ? styles.errorRow
                                                : undefined
                                        }
                                    >
                                        {columns.map((column) => (
                                            <UserTableCell key={column.key}>
                                                {renderCell(row, column.key)}
                                            </UserTableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {requests.length === 0 && (
                            <div className={styles.emptyState}>
                                <Typography>
                                    No {status.toLowerCase()}{' '}
                                    {requestType.toLowerCase()} requests.
                                </Typography>
                            </div>
                        )}
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={pageNo}
                        rowsPerPage={pageSize}
                        onChangePage={handleChangePage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        rowsPerPageOptions={
                            totalCount > 20 ? [20, 50, 100] : []
                        }
                    />
                </>
            </Spinner>

            {decisionRow && (
                <ApprovalDecisionModal
                    open
                    row={decisionRow}
                    rehireConfirmationRequired={rehireConfirmationRequired}
                    canDecide={canDecide}
                    onClose={() => setDecisionRow(null)}
                    onApprove={handleApprove}
                    onReject={handleReject}
                />
            )}
        </Paper>
    );
};

export default ApprovalWorkflowPage;
